import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SIDEBAR, ALL_PAGES, PAGE_META, DEFAULT_PAGE, PAGE_ALIASES } from './docs/manifest';
import PAGES from './docs';
import { DISCORD_URL } from '../constants/siteInfo';
import SiteIcon from '../components/ui/SiteIcon';

/**
 * A dokumentáció VÁZA: menü, kereső, morzsa, „On this page”, előző/következő —
 * a jóváhagyott sablon szerint (docs.html, 2026-10-04): a konténeren belül három
 * oszlop (menü 236 px · cikk legfeljebb 740 px · tartalomjegyzék 200 px).
 * 900 px alatt a menü a cikk fölé kerül, „All pages” gombbal nyitható.
 *
 * A tartalom nincs itt — minden doksi-oldal saját fájl a `pages/docs/` alatt,
 * a sorrendjüket és a menüt a `docs/manifest.js` írja le. Egy oldal
 * újraírásához elég azt az egy fájlt kicserélni.
 */

const slug = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export default function DocsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [sideOpen, setSideOpen] = useState(false);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const articleRef = useRef(null);
  const searchRef = useRef(null);

  // A megszűnt id-k (pl. a régi `misc`) az utódjukra mutatnak — lásd manifest.js.
  const requestedPage = searchParams.get('page') || DEFAULT_PAGE;
  const activePage = PAGE_ALIASES[requestedPage] || requestedPage;

  const navigate = (pageId) => {
    setSearchParams({ page: pageId });
    setSideOpen(false);
    window.scrollTo({ top: 0 });
  };

  // A menü szűrése a keresőmezővel.
  const filteredSidebar = query.trim()
    ? SIDEBAR.map(section => ({
        ...section,
        items: section.items.filter(item =>
          item.label.toLowerCase().includes(query.toLowerCase())
        ),
      })).filter(s => s.items.length > 0)
    : SIDEBAR;

  const PageComponent = PAGES[activePage] || PAGES[DEFAULT_PAGE];
  const meta = PAGE_META[activePage] || PAGE_META[DEFAULT_PAGE];

  // Oldalváltáskor a lap tetejére.
  useEffect(() => { window.scrollTo({ top: 0 }); }, [activePage]);

  // „/” a keresőbe ugrik (ha épp nem egy mezőben gépel).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // „On this page”: a cikk <h2>-iből, futásidőben (mint a jogi oldalakon).
  useEffect(() => {
    const root = articleRef.current;
    if (!root) return undefined;
    const headings = Array.from(root.querySelectorAll('h2'));
    headings.forEach(h => {
      if (!h.id) h.id = slug(h.textContent || '');
      h.classList.add('scroll-mt-nav');
    });
    setToc(headings.map(h => ({ id: h.id, text: h.textContent })));
    setActiveId(headings[0]?.id || null);

    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-88px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach(h => io.observe(h));
    return () => io.disconnect();
  }, [activePage]);

  return (
    <div className="wrap pt-8 sm:pt-10 pb-20 sm:pb-28 grid gap-6 min-[900px]:gap-12 items-start
                    min-[900px]:grid-cols-[236px_minmax(0,1fr)] min-[1101px]:grid-cols-[236px_minmax(0,1fr)_200px]">

      {/* ── Menü ── */}
      <aside aria-label="Documentation"
             className="min-[900px]:sticky min-[900px]:top-[84px] min-[900px]:max-h-[calc(100vh-100px)]
                        min-[900px]:overflow-y-auto min-[900px]:pr-1.5 [scrollbar-width:thin]">
        <div className="relative mb-3 min-[900px]:mb-[18px]">
          <SiteIcon name="search" className="absolute left-[11px] top-[11px] w-4 h-4 text-gray-600 pointer-events-none" />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search the docs"
            aria-label="Search the docs"
            value={query}
            onChange={e => { setQuery(e.target.value); if (e.target.value) setSideOpen(true); }}
            className="input h-[38px] pl-[34px] pr-8 text-[13px]"
          />
          <kbd className="absolute right-2 top-[9px] px-[5px] text-[11px] text-gray-500">/</kbd>
        </div>

        <button type="button" onClick={() => setSideOpen(o => !o)} aria-expanded={sideOpen}
                className="min-[900px]:hidden btn-glass w-full justify-between">
          <span className="truncate">{sideOpen ? 'Hide pages' : `All pages · ${meta.label}`}</span>
          <SiteIcon name="chevron" className={`w-4 h-4 transition-transform ${sideOpen ? 'rotate-180' : ''}`} />
        </button>

        <nav className={`${sideOpen ? 'block' : 'hidden'} min-[900px]:block mt-3.5 min-[900px]:mt-0`}>
          {filteredSidebar.map(section => (
            <div key={section.title} className="mb-5">
              <h5 className="mb-1.5 pl-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-gray-600">{section.title}</h5>
              {section.items.map(item => {
                const on = activePage === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(item.id)}
                    aria-current={on ? 'page' : undefined}
                    className={`block w-full px-2.5 py-[5px] rounded-lg text-left text-sm transition-colors duration-150 ${
                      on
                        ? 'bg-gray-850 text-gray-50 shadow-[inset_2px_0_0_#a48bff]'
                        : 'text-gray-500 hover:text-gray-100 hover:bg-white/[0.04]'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
          {filteredSidebar.length === 0 && (
            <p className="px-2.5 text-sm text-gray-500">No results for "{query}"</p>
          )}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer"
             className="mt-1 px-2.5 py-[5px] flex items-center justify-between rounded-lg text-sm text-gray-500
                        hover:text-gray-100 hover:bg-white/[0.04] transition-colors">
            Discord <span aria-hidden="true">↗</span>
          </a>
        </nav>
      </aside>

      {/* ── Cikk ── */}
      <article ref={articleRef} className="min-w-0 max-w-[740px]">
        <nav aria-label="Breadcrumb" className="pb-3.5 flex items-center gap-2 font-mono text-xs text-gray-500">
          <span>{meta.section}</span>
          <span>/</span>
          <span className="text-gray-100">{meta.label}</span>
        </nav>

        <PageComponent navigate={navigate} />

        <PrevNextNav activePage={activePage} navigate={navigate} />
      </article>

      {/* ── Ezen az oldalon ── */}
      <nav aria-label="On this page" className="hidden min-[1101px]:flex sticky top-[84px] flex-col gap-0.5">
        {toc.length > 0 && (
          <>
            <h5 className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-gray-600">On this page</h5>
            {toc.map(t => (
              <a key={t.id} href={`#${t.id}`}
                 className={`py-1 pl-3 border-l text-[13px] leading-snug transition-colors duration-150 ${
                   activeId === t.id ? 'text-gray-50 border-brand-500' : 'text-gray-500 border-line-faint hover:text-gray-100'
                 }`}>
                {t.text}
              </a>
            ))}
          </>
        )}
        <div className="mt-5 p-3.5 rounded-panel border border-line text-[13px] text-gray-500">
          Stuck? Ask on{' '}
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Discord</a>.
        </div>
      </nav>
    </div>
  );
}

function PrevNextNav({ activePage, navigate }) {
  const allPages = ALL_PAGES;
  const idx = allPages.findIndex(p => p.id === activePage);
  const prev = idx > 0 ? allPages[idx - 1] : null;
  const next = idx < allPages.length - 1 ? allPages[idx + 1] : null;

  if (!prev && !next) return null;

  const cell = 'px-4 py-3.5 flex flex-col gap-0.5 rounded-panel border border-line transition-colors duration-150 hover:border-line-strong hover:bg-gray-900';

  return (
    <div className="mt-12 pt-6 border-t border-line-faint grid gap-4 sm:grid-cols-2">
      {prev ? (
        <button type="button" onClick={() => navigate(prev.id)} className={`${cell} text-left`}>
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-gray-500">← Previous</span>
          <b className="text-[15px] font-semibold text-gray-50">{prev.label}</b>
        </button>
      ) : <div className="hidden sm:block" />}

      {next ? (
        <button type="button" onClick={() => navigate(next.id)} className={`${cell} text-right`}>
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-gray-500">Next →</span>
          <b className="text-[15px] font-semibold text-gray-50">{next.label}</b>
        </button>
      ) : <div className="hidden sm:block" />}
    </div>
  );
}
